import { useState, useEffect } from 'react';
import { Box, Button, ButtonBase, Typography, InputBase, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CategoryIcon from '@mui/icons-material/Category';
import QrCodeIcon from '@mui/icons-material/QrCode';
import StraightenIcon from '@mui/icons-material/Straighten';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import HSNModule from './HSNModule.jsx';
import TaxModule from './TaxModule.jsx';
import TDSDetails from './TDSDetails.jsx';
import DepreciationDetails from './DepreciationDetails.jsx';
import UnitModule from './UnitModule.jsx';
import StatusModule from './StatusModule.jsx';
import SourceModule from './SourceModule.jsx';
import TransportModule from './TransportModule.jsx';
import SeriesModule from './SeriesModule.jsx';
import CurrencyDetail2 from './CurrencyDetail2.jsx';
import ProdGroupDetail2 from './ProdGroupDetails2.jsx';
import AccGrpDetails from './AccGrpDetails.jsx';
import ConsignerMaster from './ConsignerMaster.jsx';
import TermsConditionModule from './TermsConditionModule.jsx';
import LoadingOverlay from '../components/LoadingOverlay.jsx';

// ── Design tokens (Facebook blue · grey 200 · arctic white) ─────────────────
const C = {
    blue: '#1877F2',
    blueDark: '#0A66C2',
    tint: '#E7F0FE',
    ink: '#0f172a',
    sub: '#64748b',
    mute: '#94a3b8',
    line: '#E4E6EB',
    hover: '#F0F2F5',
    page: '#F5F7FA',
    white: '#FFFFFF',
};

// index = the case number `renderModule`'s switch uses for that module —
// kept stable even when the array is filtered by plan below.
// `group` is display-only (sidebar sections); it does not affect behaviour.
const tabOptions = [
    { label: 'Account Group',     icon: <AccountTreeIcon fontSize="small" />,         group: 'Finance' },
    { label: 'Tax',               icon: <ReceiptIcon fontSize="small" />,             group: 'Finance' },
    { label: 'TDS',               icon: <AccountBalanceIcon fontSize="small" />,      group: 'Finance', accountingOnly: true },
    { label: 'Depreciation',      icon: <TrendingDownIcon fontSize="small" />,        group: 'Finance', accountingOnly: true },
    { label: 'Series',            icon: <FormatListNumberedIcon fontSize="small" />,  group: 'Masters' },
    { label: 'Currency',          icon: <CurrencyExchangeIcon fontSize="small" />,    group: 'Finance', accountingOnly: true },
    { label: 'Product Group',     icon: <CategoryIcon fontSize="small" />,            group: 'Masters' },
    { label: 'HSN',               icon: <QrCodeIcon fontSize="small" />,              group: 'Masters' },
    { label: 'Unit',              icon: <StraightenIcon fontSize="small" />,          group: 'Masters' },
    { label: 'Transport',         icon: <LocalShippingIcon fontSize="small" />,       group: 'Logistics & Terms' },
    { label: 'Consigner',         icon: <BusinessIcon fontSize="small" />,            group: 'Logistics & Terms' },
    { label: 'Terms & Conditions',icon: <GavelIcon fontSize="small" />,               group: 'Logistics & Terms' },
].map((tab, index) => ({ ...tab, key: index }));

const GROUP_ORDER = ['Finance', 'Masters', 'Logistics & Terms'];

const CompanyDataManager = ({ companyId, planName, setSnackbar, onBack, connectionString }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [mode, setMode] = useState('comp');
    const [isLoading, setIsLoading] = useState(false);
    const [moduleQuery, setModuleQuery] = useState('');   // sidebar filter (display only)
    const theme = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));  // < 600px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));    // ≥ 900px

    // "Billing" plan -> hide TDS, Depreciation, Currency. "Accounting" plan
    // (or any other/unknown plan, e.g. planName not yet resolved) -> show
    // every module, unchanged.
    const visibleTabOptions = planName === 'Billing'
        ? tabOptions.filter(tab => !tab.accountingOnly)
        : tabOptions;

    // If the currently-selected module isn't visible for this plan (e.g. the
    // company being edited turned out to be on the Billing plan after all),
    // fall back to the first module the plan allows.
    useEffect(() => {
        if (visibleTabOptions.length > 0 && !visibleTabOptions.some(t => t.key === selectedTab)) {
            setSelectedTab(visibleTabOptions[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTabOptions]);

    const moduleProps = { companyId, connectionString, setSnackbar, mode, setMode, setIsLoading };

    const renderModule = () => {
        switch (selectedTab) {
            case 0:  return <AccGrpDetails {...moduleProps} />;
            case 1:  return <TaxModule {...moduleProps} />;
            case 2:  return <TDSDetails {...moduleProps} />;
            case 3:  return <DepreciationDetails {...moduleProps} />;
            case 4:  return <SeriesModule {...moduleProps} />;
            case 5:  return <CurrencyDetail2 {...moduleProps} />;
            case 6:  return <ProdGroupDetail2 {...moduleProps} />;
            case 7:  return <HSNModule {...moduleProps} />;
            case 8:  return <UnitModule {...moduleProps} />;
            case 9:  return <TransportModule {...moduleProps} />;
            case 10: return <ConsignerMaster {...moduleProps} />;
            case 11: return <TermsConditionModule {...moduleProps} />;
            default: return null;
        }
    };

    const activeTab = tabOptions.find(t => t.key === selectedTab);

    // Sidebar groups (filtered by the search box; display only)
    const q = moduleQuery.trim().toLowerCase();
    const sidebarGroups = GROUP_ORDER
        .map(group => ({ group, items: visibleTabOptions.filter(t => t.group === group && (!q || t.label.toLowerCase().includes(q))), }))
        .filter(g => g.items.length > 0);

    const panelAnim = { animation: 'cdmPanelIn .38s cubic-bezier(.22,1,.36,1) backwards' };

    return (
        <Box sx={{ px: { xs: 0, sm: 1, md: 2 }, py: { xs: 0, md: 1 } }}>
            <style>{[
                '@keyframes cdmPanelIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }',
                '@keyframes cdmItemIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: none; } }',
                '@keyframes cdmHeaderIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }',
            ].join('\n')}</style>

            {/* ── Header ─────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: { xs: 1.5, sm: 0 }, py: { xs: 1.5, md: 1.5 }, mb: { xs: 0.5, md: 1.5 }, animation: 'cdmHeaderIn .4s ease backwards', }}>
                <Button startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />} onClick={onBack} size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600, color: C.sub, bgcolor: C.white, border: `1px solid ${C.line}`, borderRadius: 2, px: 1.6, py: 0.7, transition: 'all .2s ease', '&:hover': { bgcolor: C.hover, color: C.ink, transform: 'translateX(-2px)' }, }}
                >
                    {isMobile ? 'Back' : 'Back to Company'}
                </Button>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: C.mute, fontSize: '0.74rem', fontWeight: 500 }}>
                        <span>Company</span>
                        <ChevronRightIcon sx={{ fontSize: 14 }} />
                        <span>Data Management</span>
                        {activeTab && (
                            <>
                                <ChevronRightIcon sx={{ fontSize: 14 }} />
                                <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>{activeTab.label}</Box>
                            </>
                        )}
                    </Box>
                    <Typography sx={{ color: C.ink, fontWeight: 700, letterSpacing: '-0.3px', fontSize: { xs: '1.1rem', md: '1.4rem' }, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', }} >
                        Company Data Management
                    </Typography>
                </Box>

                {!isMobile && (
                    <Box sx={{ px: 1.4, py: 0.5, borderRadius: 5, bgcolor: C.tint, color: C.blueDark, fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap', }}>
                        {visibleTabOptions.length} modules
                    </Box>
                )}
            </Box>

            {/* ── Mobile + Tablet: scrollable pill navigation ─────────── */}
            {!isDesktop && (
                <Box sx={{
                    display: 'flex', gap: 0.9, overflowX: 'auto', pb: 1.5, px: { xs: 1.5, sm: 0 },
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: C.line, borderRadius: 2 },
                }}>
                    {visibleTabOptions.map((tab, i) => {
                        const active = selectedTab === tab.key;
                        return (
                            <ButtonBase key={tab.key} onClick={() => setSelectedTab(tab.key)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.7, flexShrink: 0, whiteSpace: 'nowrap',
                                    px: 1.6, py: 0.9, borderRadius: 5, fontSize: '0.8rem', fontWeight: active ? 700 : 500,
                                    color: active ? '#fff' : C.sub,
                                    background: active ? `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDark} 100%)` : C.white,
                                    border: `1px solid ${active ? C.blue : C.line}`,
                                    boxShadow: active ? '0 4px 12px rgba(24,119,242,0.28)' : 'none',
                                    transition: 'all .22s ease',
                                    animation: `cdmItemIn .35s ease ${i * 0.03}s backwards`,
                                    '& svg': { fontSize: '1rem' },
                                    '&:hover': { borderColor: C.blue, color: active ? '#fff' : C.blue },
                                }}
                            >
                                {tab.icon}
                                {tab.label}
                            </ButtonBase>
                        );
                    })}
                </Box>
            )}

            {/* ── Desktop: sidebar + content ──────────────────────────── */}
            {isDesktop ? (
                <Box sx={{
                    display: 'flex', borderRadius: 3, overflow: 'hidden', minHeight: 600,
                    bgcolor: C.white, border: `1px solid ${C.line}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.05)',
                }}>
                    {/* Sidebar */}
                    <Box sx={{
                        width: 252, flexShrink: 0, bgcolor: '#FAFBFC', borderRight: `1px solid ${C.line}`,
                        display: 'flex', flexDirection: 'column', p: 1.5,
                    }}>
                        {/* Module search */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.5, mb: 1.5,
                            bgcolor: C.white, border: `1px solid ${C.line}`, borderRadius: 2,
                            transition: 'border-color .2s ease, box-shadow .2s ease',
                            '&:focus-within': { borderColor: C.blue, boxShadow: '0 0 0 3px rgba(24,119,242,0.12)' },
                        }}>
                            <SearchIcon sx={{ fontSize: 18, color: C.mute }} />
                            <InputBase
                                placeholder="Find module"
                                value={moduleQuery}
                                onChange={(e) => setModuleQuery(e.target.value)}
                                sx={{ flex: 1, fontSize: '0.84rem', color: C.ink }}
                            />
                        </Box>

                        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.25 }}>
                            {sidebarGroups.length === 0 && (
                                <Typography sx={{ color: C.mute, fontSize: '0.8rem', px: 1, py: 1 }}>No modules match</Typography>
                            )}
                            {sidebarGroups.map((g, gi) => (
                                <Box key={g.group} sx={{ mb: 1.75 }}>
                                    <Typography sx={{
                                        px: 1, pb: 0.6, color: C.mute, fontSize: '0.66rem', fontWeight: 700,
                                        letterSpacing: '1px', textTransform: 'uppercase',
                                    }}>
                                        {g.group}
                                    </Typography>
                                    {g.items.map((tab, i) => {
                                        const active = selectedTab === tab.key;
                                        return (
                                            <ButtonBase
                                                key={tab.key}
                                                onClick={() => setSelectedTab(tab.key)}
                                                sx={{
                                                    position: 'relative', width: '100%', justifyContent: 'flex-start',
                                                    gap: 1.25, px: 1, py: 0.8, mb: 0.25, borderRadius: 2, textAlign: 'left',
                                                    bgcolor: active ? C.tint : 'transparent',
                                                    color: active ? C.blueDark : C.ink,
                                                    transition: 'background-color .2s ease, color .2s ease',
                                                    animation: `cdmItemIn .35s ease ${(gi * 3 + i) * 0.03}s backwards`,
                                                    '&::before': {
                                                        content: '""', position: 'absolute', left: 0, top: '22%', bottom: '22%', width: 3,
                                                        borderRadius: 3, bgcolor: C.blue,
                                                        transform: active ? 'scaleY(1)' : 'scaleY(0)',
                                                        transition: 'transform .25s ease',
                                                    },
                                                    '&:hover': { bgcolor: active ? C.tint : C.hover },
                                                    '&:hover .cdm-tile': { transform: 'scale(1.06)' },
                                                }}
                                            >
                                                <Box className="cdm-tile" sx={{
                                                    width: 30, height: 30, borderRadius: 1.75, flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    bgcolor: active ? C.blue : C.hover,
                                                    color: active ? '#fff' : C.sub,
                                                    transition: 'all .2s ease',
                                                }}>
                                                    {tab.icon}
                                                </Box>
                                                <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: active ? 700 : 500 }}>
                                                    {tab.label}
                                                </Typography>
                                            </ButtonBase>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Module content */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: C.white }}>
                        {/* Re-keyed so each module fades in when you switch */}
                        <Box key={selectedTab} sx={{ flex: 1, overflow: 'auto', p: { md: 2, lg: 2.5 }, width: '100%', boxSizing: 'border-box', ...panelAnim }}>
                            {renderModule()}
                        </Box>
                    </Box>
                </Box>
            ) : (
                /* Mobile + Tablet: content below nav */
                <Box key={selectedTab} sx={{
                    mx: { xs: 1, sm: 0 }, p: { xs: 1, sm: 1.5 }, bgcolor: C.white, borderRadius: 3,
                    border: `1px solid ${C.line}`, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', ...panelAnim,
                }}>
                    {renderModule()}
                </Box>
            )}

            {/* Loading Overlay */}
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
        </Box>
    );
};

export default CompanyDataManager;
